import type { Application, CreateApplication } from "@updaterelay/contracts";
import { useEffect, useState } from "react";
import { Modal } from "./Modal";

interface ApplicationDialogProps {
  open: boolean;
  application: Application | null;
  busy: boolean;
  error?: string;
  onClose: () => void;
  onSave: (input: CreateApplication) => Promise<void>;
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/gu, "-")
    .replace(/^-|-$/gu, "");
}

export function ApplicationDialog({
  open,
  application,
  busy,
  error,
  onClose,
  onSave,
}: ApplicationDialogProps) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(application?.name ?? "");
    setSlug(application?.slug ?? "");
    setDescription(application?.description ?? "");
    setSlugEdited(Boolean(application));
  }, [application, open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow="Application registry"
      title={application ? "Edit application" : "Add application"}
    >
      <form
        className="form-stack"
        onSubmit={async (event) => {
          event.preventDefault();
          await onSave({ name, slug, description: description.trim() || null });
        }}
      >
        <label className="field">
          <span>Name</span>
          <input
            required
            maxLength={120}
            value={name}
            placeholder="Northstar Desktop"
            onChange={(event) => {
              const nextName = event.target.value;
              setName(nextName);
              if (!slugEdited) setSlug(slugify(nextName));
            }}
          />
        </label>
        <label className="field">
          <span>Public identifier</span>
          <div className="input-prefix">
            <span>/</span>
            <input
              required
              minLength={2}
              maxLength={64}
              pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
              value={slug}
              placeholder="northstar-desktop"
              onChange={(event) => {
                setSlug(event.target.value.toLowerCase());
                setSlugEdited(true);
              }}
            />
          </div>
          <small>
            Used in the installer-facing update endpoint. Changing it breaks old clients.
          </small>
        </label>
        <label className="field">
          <span>Description</span>
          <textarea
            rows={3}
            maxLength={1_000}
            value={description}
            placeholder="What this release signal is used for."
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>
        {error ? <p className="form-error">{error}</p> : null}
        <div className="modal__actions">
          <button className="button button--ghost" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="button button--primary" type="submit" disabled={busy}>
            {busy ? "Saving…" : application ? "Save changes" : "Add application"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
